export interface AvatarBitmap {
  width: number;
  height: number;
  /**
   * Packed bitmap bytes, row-major, 8 pixels per byte, MSB-first.
   * Bit 7 of byte 0 is pixel (0,0), bit 6 is (1,0), etc.
   */
  bits: Uint8Array;
}

const AVATAR_SIZE = 16;

/** High-entropy 32-bit hash from string (djb2-like with extra mixing). */
function hash32(s: string, seed: number): number {
  let h = (seed * 0x811c9dc5) >>> 0;
  for (let i = 0; i < s.length; i += 1) {
    h = Math.imul(h ^ s.charCodeAt(i), 0x01000193);
    h = (h >>> 0) ^ ((h << 13) | (h >>> 19));
  }
  return h >>> 0;
}

/** Returns 8 uint32 values from username for avatar variation. */
function multiHash(username: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < 8; i += 1) {
    out.push(hash32(username, i + 1));
  }
  return out;
}

function setPixel(bits: Uint8Array, width: number, x: number, y: number, on: boolean): void {
  if (x < 0 || x >= width || y < 0 || y >= width) return;
  const i = y * width + x;
  const byteIndex = i >> 3;
  const bitIndex = 7 - (i & 7);
  if (on) {
    bits[byteIndex] |= 1 << bitIndex;
  } else {
    bits[byteIndex] &= ~(1 << bitIndex);
  }
}

/** Rotate point (x,y) around (cx,cy) by angle in 90° steps (0..3). */
function rotate90(x: number, y: number, cx: number, cy: number, steps: number): [number, number] {
  let dx = x - cx;
  let dy = y - cy;
  for (let k = 0; k < (steps % 4 + 4) % 4; k += 1) {
    const t = dx;
    dx = -dy;
    dy = t;
  }
  return [cx + dx, cy + dy];
}

/**
 * Generate a deterministic avatar bitmap from username.
 * Uses high-entropy hash to pick pattern (dots, plus, minus, cross), spacing, inclination, etc.
 */
export function generateAvatar(username: string): AvatarBitmap {
  const h = multiHash(username || "?");
  const W = AVATAR_SIZE;
  const byteCount = (W * W) >> 3;
  const bits = new Uint8Array(byteCount);

  const pattern = h[0] % 4;
  const spacing = (h[1] % 3) + 1;
  const inclination = h[2] % 4;
  const invert = (h[3] & 1) === 1;
  const thick = (h[4] % 2) === 1;
  const symmetry = (h[5] & 1) === 1;
  const cx = (W - 1) / 2;
  const cy = (W - 1) / 2;

  const clamp = (v: number) => Math.max(0, Math.min(W - 1, Math.round(v)));
  const draw = (x: number, y: number, on: boolean) => {
    const [rx, ry] = rotate90(x, y, cx, cy, inclination);
    setPixel(bits, W, clamp(rx), clamp(ry), on);
    if (symmetry) {
      const [sx, sy] = rotate90(W - 1 - x, y, cx, cy, inclination);
      setPixel(bits, W, clamp(sx), clamp(sy), on);
    }
  };

  if (pattern === 0) {
    for (let y = 0; y < W; y += 1) {
      for (let x = 0; x < W; x += 1) {
        const v = hash32(`${username}:${x}:${y}`, h[6]) % spacing === 0;
        draw(x, y, invert ? !v : v);
      }
    }
  } else if (pattern === 1) {
    const half = thick ? 2 : 1;
    for (let y = 0; y < W; y += 1) {
      for (let x = 0; x < W; x += 1) {
        const on = Math.abs(x - cx) < half || Math.abs(y - cy) < half;
        draw(x, y, invert ? !on : on);
      }
    }
  } else if (pattern === 2) {
    const half = thick ? 2 : 1;
    for (let y = 0; y < W; y += 1) {
      for (let x = 0; x < W; x += 1) {
        const on = Math.abs(y - cy) < half;
        draw(x, y, invert ? !on : on);
      }
    }
  } else {
    for (let y = 0; y < W; y += 1) {
      for (let x = 0; x < W; x += 1) {
        const on = Math.abs((x - cx) - (y - cy)) < 1 || Math.abs((x - cx) + (y - cy)) < 1;
        draw(x, y, invert ? !on : on);
      }
    }
  }

  return { width: W, height: W, bits };
}

/**
 * Return the avatar image as received in the log (state), or a generated avatar from username.
 */
export function getUserAvatar(
  username: string,
  avatarsByUsername?: Record<string, AvatarBitmap> | null,
): AvatarBitmap {
  const stored = avatarsByUsername?.[username ?? ""];
  if (stored) return stored;
  return generateAvatar(username ?? "?");
}

export function decodeAvatarFromEventData(data: unknown): AvatarBitmap | null {
  if (!data || typeof data !== "object") return null;
  const anyData = data as { bitmap?: unknown; width?: unknown; height?: unknown };
  const { bitmap, width, height } = anyData;
  if (
    typeof bitmap !== "string" ||
    typeof width !== "number" ||
    typeof height !== "number" ||
    !bitmap
  ) {
    return null;
  }

  // base64 (no gzip) -> raw bytes
  let binary = "";
  try {
    binary = atob(bitmap);
  } catch {
    return null;
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return {
    width,
    height,
    bits: bytes,
  };
}

